import { ButtonProps } from '@toggle/design-system';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetSubscription,
  usePostSubscription,
} from '~/react-query-hooks/events/use-event-alerts/useEventAlerts';
import { ToastPosition, useToasts } from '~/stores/use-toasts/useToasts';

import { TrackingToastContent } from '../tracking-toast/TrackingToast';
import {
  getTrackButtonProps,
  TRACKING_TOAST_ID,
  TRACKING_TOAST_SCOPE,
} from '../utils';
import * as S from './TrackScenarioInsightButton.styles';

export interface TrackScenarioInsightButtonProps {
  insightId: string;
}

export const TrackScenarioInsightButton = ({
  insightId,
}: TrackScenarioInsightButtonProps) => {
  const { t } = useTranslation('insights');
  const { showToast, clearScopedToasts } = useToasts(state => ({
    showToast: state.showToast,
    clearScopedToasts: state.clearScopedToasts,
  }));

  useEffect(() => {
    return () => clearScopedToasts(TRACKING_TOAST_SCOPE);
  }, []);

  // TODO: use insightId as source_id once endpoints are live
  const mockSourceId = 'mockInsightId';
  const { data: subscription } = useGetSubscription(mockSourceId, 'insights');
  const { mutate: updateSubscription } = usePostSubscription();

  const isTracked = subscription?.tracking_status === 'active';

  const handleTrackClick = () => {
    const nextTracked = !isTracked;
    updateSubscription(
      {
        source_id: mockSourceId,
        source_type: 'insights',
        track: nextTracked,
      },
      {
        onSuccess: () => {
          if (nextTracked) {
            showToast({
              id: TRACKING_TOAST_ID,
              scope: TRACKING_TOAST_SCOPE,
              timeout: 5000,
              position: ToastPosition.TopRight,
              content: <TrackingToastContent insightId={insightId} />,
            });
          } else {
            clearScopedToasts(TRACKING_TOAST_SCOPE);
          }
        },
      }
    );
  };

  if (!subscription) {
    return null;
  }

  const buttonProps: ButtonProps = getTrackButtonProps(isTracked, t);

  return (
    <S.StyledButton
      $isTracked={isTracked}
      {...buttonProps}
      onClick={handleTrackClick}
    />
  );
};
